import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Shield, Clock, AlertCircle, CheckCircle2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

interface PlatformAcknowledgmentProps {
  children: React.ReactNode;
}

export const PlatformAcknowledgment = ({ children }: PlatformAcknowledgmentProps) => {
  const [hasAcknowledged, setHasAcknowledged] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const acknowledged = localStorage.getItem("platform_acknowledged");
    if (acknowledged === "true") {
      setHasAcknowledged(true);
    } else {
      setIsOpen(true);
    }
  }, []);

  const handleAcknowledge = () => {
    localStorage.setItem("platform_acknowledged", "true");
    setHasAcknowledged(true);
    setIsOpen(false);
  };

  if (hasAcknowledged) {
    return <>{children}</>;
  }

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto [&>button]:hidden">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2 text-primary">
            <Shield className="h-6 w-6" />
            Important: Please Read Before Continuing
          </DialogTitle>
          <DialogDescription className="text-base">
            You must understand how our platform works before accessing your dashboard.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Multi-Step Verification */}
          <Card className="p-4 border-primary/30 bg-primary/5">
            <h3 className="font-semibold flex items-center gap-2 mb-2">
              <Clock className="h-5 w-5 text-primary" />
              Multi-Step Verification Process
            </h3>
            <p className="text-sm text-muted-foreground mb-3">
              This platform operates through a <strong>legitimate affiliate approval system</strong>. 
              Payments do not occur immediately after completing one task.
            </p>
            <div className="text-sm text-muted-foreground space-y-1 ml-4">
              <p>Each task must be approved by:</p>
              <ol className="list-decimal list-inside ml-2 space-y-1">
                <li>The advertiser who created the offer</li>
                <li>The affiliate network managing the campaign</li>
                <li>Our platform for final verification</li>
              </ol>
              <p className="mt-2">
                This process typically takes <strong>about one week</strong>.
              </p>
            </div>
          </Card>

          {/* Security Standards */}
          <Card className="p-4 border-destructive/30 bg-destructive/5">
            <h3 className="font-semibold flex items-center gap-2 mb-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Strict Security Standards
            </h3>
            <Alert variant="destructive" className="mb-3">
              <AlertDescription className="text-sm">
                The following lead to <strong>immediate disqualification</strong>:
              </AlertDescription>
            </Alert>
            <ul className="text-sm text-muted-foreground space-y-1 ml-4">
              <li className="flex items-start gap-2">
                <span className="text-destructive font-bold">×</span>
                Using VPNs or proxy services
              </li>
              <li className="flex items-start gap-2">
                <span className="text-destructive font-bold">×</span>
                Using bots or automated tools
              </li>
              <li className="flex items-start gap-2">
                <span className="text-destructive font-bold">×</span>
                Providing fake information
              </li>
              <li className="flex items-start gap-2">
                <span className="text-destructive font-bold">×</span>
                Attempting to bypass verification systems
              </li>
            </ul>
            <div className="mt-3 p-2 bg-primary/10 rounded text-sm">
              <CheckCircle2 className="h-4 w-4 inline mr-1 text-primary" />
              Complete tasks honestly to ensure your earnings are processed.
            </div>
          </Card>

          {/* Acknowledgment Checkbox */}
          <div className="flex items-start space-x-3 p-4 border rounded-lg bg-muted/50">
            <Checkbox 
              id="acknowledge" 
              checked={isChecked}
              onCheckedChange={(checked) => setIsChecked(checked === true)}
            />
            <label htmlFor="acknowledge" className="text-sm font-medium leading-relaxed cursor-pointer">
              I have read and understood how the platform works, including the multi-step verification process, 
              payment timeline, and security standards. I agree to follow all rules.
            </label>
          </div>

          <Button 
            onClick={handleAcknowledge} 
            disabled={!isChecked}
            className="w-full"
            size="lg"
          >
            I Understand, Continue to Dashboard
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
